import { useRef, useCallback, useState } from 'react';

export interface ScrollState {
  isScrolling: boolean;
  isPaused: boolean;
  currentPosition: number;
  scrollRate: number; // pixels per second
}

/**
 * Calculate the scroll rate (pixels per second) from song tempo, length, and content height.
 *
 * The total song duration in seconds is derived from lengthMinutes + lengthSeconds.
 * Tempo is accepted for future tempo-weighted scrolling but the current formula uses
 * a uniform rate: scrollRate = contentHeight / durationInSeconds.
 *
 * Returns 0 when inputs are invalid (zero duration or non-positive height).
 */
export function calculateScrollRate(
  _tempo: number,
  lengthMinutes: number,
  lengthSeconds: number,
  contentHeight: number,
): number {
  const durationSec = lengthMinutes * 60 + lengthSeconds;
  if (durationSec <= 0 || contentHeight <= 0) return 0;
  return contentHeight / durationSec;
}

/**
 * Hook that drives requestAnimationFrame-based smooth scrolling for the
 * performance view.  Exposes start / pause / resume / stop / setPosition
 * controls and reports the current ScrollState.
 *
 * @param onPositionChange  Called every animation frame with the new pixel position.
 * @param maxPosition       The maximum scroll position (content height − viewport height).
 */
export function useAutoScroll(
  onPositionChange: (position: number) => void,
  maxPosition: number,
) {
  const [state, setState] = useState<ScrollState>({
    isScrolling: false,
    isPaused: false,
    currentPosition: 0,
    scrollRate: 0,
  });

  // Mutable refs so the rAF loop always sees the latest values without re-creating callbacks.
  const rafId = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const startPositionRef = useRef<number>(0);
  const scrollRateRef = useRef<number>(0);
  const maxPosRef = useRef<number>(maxPosition);
  maxPosRef.current = maxPosition;

  // Keep a ref-copy of onPositionChange to avoid stale closures.
  const onPosRef = useRef(onPositionChange);
  onPosRef.current = onPositionChange;

  const cancelRaf = useCallback(() => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  }, []);

  const tick = useCallback((timestamp: number) => {
    const elapsed = (timestamp - startTimeRef.current) / 1000; // seconds
    let newPos = startPositionRef.current + elapsed * scrollRateRef.current;

    // Clamp to [0, maxPosition]
    if (newPos < 0) newPos = 0;
    if (newPos >= maxPosRef.current) {
      newPos = maxPosRef.current;
      // Reached the end — stop scrolling.
      cancelRaf();
      setState((s) => ({ ...s, isScrolling: false, isPaused: false, currentPosition: newPos }));
      onPosRef.current(newPos);
      return;
    }

    onPosRef.current(newPos);
    setState((s) => ({ ...s, currentPosition: newPos }));
    rafId.current = requestAnimationFrame(tick);
  }, [cancelRaf]);

  /**
   * Begin scrolling from the current position at the given rate.
   */
  const start = useCallback(
    (rate: number) => {
      cancelRaf();
      scrollRateRef.current = rate;
      startPositionRef.current = 0;
      startTimeRef.current = performance.now();

      setState({
        isScrolling: true,
        isPaused: false,
        currentPosition: 0,
        scrollRate: rate,
      });
      onPosRef.current(0);
      rafId.current = requestAnimationFrame(tick);
    },
    [cancelRaf, tick],
  );

  /**
   * Pause scrolling, saving the current position.
   */
  const pause = useCallback(() => {
    cancelRaf();
    setState((s) => {
      if (!s.isScrolling) return s;
      return { ...s, isScrolling: false, isPaused: true };
    });
  }, [cancelRaf]);

  /**
   * Resume scrolling from the paused position.
   */
  const resume = useCallback(() => {
    setState((prev) => {
      if (!prev.isPaused) return prev;
      startPositionRef.current = prev.currentPosition;
      startTimeRef.current = performance.now();
      rafId.current = requestAnimationFrame(tick);
      return { ...prev, isScrolling: true, isPaused: false };
    });
  }, [tick]);

  /**
   * Stop scrolling and reset to the beginning.
   */
  const stop = useCallback(() => {
    cancelRaf();
    setState({
      isScrolling: false,
      isPaused: false,
      currentPosition: 0,
      scrollRate: 0,
    });
    onPosRef.current(0);
  }, [cancelRaf]);

  /**
   * Jump to an arbitrary position (e.g. manual scroll override).
   * If currently scrolling, the animation continues from the new position.
   */
  const setPosition = useCallback(
    (pos: number) => {
      const clamped = Math.max(0, Math.min(pos, maxPosRef.current));
      onPosRef.current(clamped);

      setState((prev) => {
        if (prev.isScrolling) {
          // Re-anchor the animation from the new position.
          startPositionRef.current = clamped;
          startTimeRef.current = performance.now();
        }
        return { ...prev, currentPosition: clamped };
      });
    },
    [],
  );

  return { state, start, pause, resume, stop, setPosition } as const;
}
